import {
  type InfiniteData,
  infiniteQueryOptions,
  mutationOptions,
  queryOptions,
} from "@tanstack/react-query";
import {
  ADD_FLEA_MARKET_FAVORITE,
  CREATE_FLEA_MARKET_TRANSACTION,
  GET_ADDRESS_FLEA_MARKET,
  GET_FLEA_MARKET_FAVORITE_LIST,
  GET_FLEA_MARKET_ITEM_DETAIL,
  GET_FLEA_MARKET_ITEM_LIST,
  GET_FLEA_MARKET_SHIPMENTS_DETAIL,
  GET_FLEA_MARKET_TRANSACTION_DETAIL,
  GET_FLEA_MARKET_TRANSACTION_LIST,
  GET_USER_INF_FOR_WEB,
  REGISTER_ADDRESS_FLEA_MARKET,
  REMOVE_FLEA_MARKET_FAVORITE,
  UPDATE_FLEA_MARKET_SHIPMENT_STATUS,
  UPDATE_FLEA_MARKET_TRANSACTION,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import { useAuthStore } from "~/stores/useAuthStore";
import type {
  FavoriteItem,
  FleaMarketItem,
  FleaMarketItemWithFavorites,
  FleaMarketSellerInfo,
  FleaMarketTransactionDetail,
  RegisterAddressPayload,
  ShipmentDetail,
  ShippingAddress,
  TransactionListResponse,
  TransactionStatus,
} from "./types";

const PAGE_SIZE = 20;
const DEFAULT_CATEGORY = "all";
// 既定では sold も含めて取得（一覧で売切れアイテムも SOLD バッジ付きで見せる方針）
const DEFAULT_SALES_STATUS = "all";

export type ItemListSearchParams = {
  category?: string;
  /** "all" 指定で sales_status フィルタを外す。未指定なら全件（sold 含む）を取得 */
  salesStatus?: string;
  sellerId?: string;
  bookmarkOnly?: boolean;
};

/** 商品一覧（無限スクロール） */
export const fleaMarketItemListInfiniteQueryOptions = (
  params: ItemListSearchParams = {},
) =>
  infiniteQueryOptions({
    queryKey: queryKeys.fleamarket.itemsList(params),
    queryFn: ({ pageParam }) => {
      const salesStatus = params.salesStatus ?? DEFAULT_SALES_STATUS;
      return apiClient<FleaMarketItemWithFavorites[]>(
        GET_FLEA_MARKET_ITEM_LIST,
        {
          category: params.category ?? DEFAULT_CATEGORY,
          limit: PAGE_SIZE,
          page: pageParam,
          ...(salesStatus !== "all" ? { sales_status: salesStatus } : {}),
          ...(params.sellerId ? { seller_id: params.sellerId } : {}),
          ...(params.bookmarkOnly !== undefined
            ? { bookmark_only: params.bookmarkOnly }
            : {}),
          ...(params.bookmarkOnly
            ? { user_id: useAuthStore.getState().userId ?? "" }
            : {}),
        },
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage && lastPage.length === PAGE_SIZE ? lastPageParam + 1 : undefined,
  });

/** 商品詳細 */
export const fleaMarketItemDetailQueryOptions = (itemId: string) =>
  queryOptions({
    queryKey: queryKeys.fleamarket.itemDetail(itemId),
    queryFn: () =>
      apiClient<FleaMarketItemWithFavorites>(GET_FLEA_MARKET_ITEM_DETAIL, {
        item_id: itemId,
      }),
  });

/** お気に入り一覧 */
export const fleaMarketFavoriteListQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: queryKeys.fleamarket.favorites(userId),
    queryFn: () =>
      apiClient<FavoriteItem[]>(GET_FLEA_MARKET_FAVORITE_LIST, {
        user_id: userId,
        page: 1,
        limit: PAGE_SIZE,
      }),
  });

/** 取引一覧 */
export const fleaMarketTransactionListQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: queryKeys.fleamarket.transactions(userId),
    queryFn: () =>
      apiClient<TransactionListResponse>(GET_FLEA_MARKET_TRANSACTION_LIST, {
        user_id: userId,
        page: 1,
        limit: PAGE_SIZE,
      }),
  });

/** 取引詳細 */
export const fleaMarketTransactionDetailQueryOptions = (
  transactionId: string,
) =>
  queryOptions({
    queryKey: queryKeys.fleamarket.transactionDetail(transactionId),
    queryFn: () =>
      apiClient<FleaMarketTransactionDetail>(
        GET_FLEA_MARKET_TRANSACTION_DETAIL,
        {
          transaction_id: transactionId,
        },
      ),
  });

/** 配送詳細 */
export const fleaMarketShipmentDetailQueryOptions = (
  userId: string,
  transactionId: string,
) =>
  queryOptions({
    queryKey: queryKeys.fleamarket.shipmentDetail(userId, transactionId),
    queryFn: () =>
      apiClient<ShipmentDetail>(GET_FLEA_MARKET_SHIPMENTS_DETAIL, {
        user_id: userId,
        transaction_id: transactionId,
      }),
  });

/** 配送先住所（レスポンスは配列で返ってくるのでデフォルト or 先頭を採用） */
export const fleaMarketAddresQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: queryKeys.fleamarket.address(userId),
    queryFn: async () => {
      const list = await apiClient<ShippingAddress[] | null>(
        GET_ADDRESS_FLEA_MARKET,
        { user_id: userId },
      );
      if (!list || list.length === 0) return null;
      return list.find((a) => a.is_default) ?? list[0] ?? null;
    },
  });

/** 出品者情報 */
export const fleaMarketSellerInfoQueryOptions = (sellerId: string) =>
  queryOptions({
    queryKey: queryKeys.fleamarket.seller(sellerId),
    queryFn: () =>
      apiClient<FleaMarketSellerInfo>(GET_USER_INF_FOR_WEB, {
        user_id: useAuthStore.getState().userId,
        req_user_id: sellerId,
      }),
  });

type FavoriteMutationVariables = { itemId: string };

// ─────────────────────────────────────────────
// いいね数（fav_count）キャッシュの楽観的更新ヘルパー
// 表示中の「♡ N」は商品詳細・商品一覧クエリの fav_count から来ており、
// favorites 一覧キャッシュ（heart の点灯判定用）とは別物。
// いいねトグル時にこちらも増減させないと、リロードまで件数が変わらない。
// ─────────────────────────────────────────────

type FavCountSnapshot = {
  itemDetail: FleaMarketItemWithFavorites | undefined;
  itemLists: [
    readonly unknown[],
    InfiniteData<FleaMarketItemWithFavorites[]> | undefined,
  ][];
};

/** 商品詳細・商品一覧（出品者別カルーセル含む）の fav_count を delta だけ増減する */
const adjustFavCountInCaches = (itemId: string, delta: number) => {
  queryClient.setQueryData<FleaMarketItemWithFavorites>(
    queryKeys.fleamarket.itemDetail(itemId),
    (old) =>
      old ? { ...old, fav_count: Math.max(0, old.fav_count + delta) } : old,
  );
  queryClient.setQueriesData<InfiniteData<FleaMarketItemWithFavorites[]>>(
    { queryKey: queryKeys.fleamarket.items() },
    (old) =>
      old
        ? {
            ...old,
            pages: old.pages.map((page) =>
              page.map((entry) =>
                entry.item.item_id === itemId
                  ? {
                      ...entry,
                      fav_count: Math.max(0, entry.fav_count + delta),
                    }
                  : entry,
              ),
            ),
          }
        : old,
  );
};

/** ロールバック用に fav_count 関連キャッシュをスナップショットする */
const snapshotFavCountCaches = (itemId: string): FavCountSnapshot => ({
  itemDetail: queryClient.getQueryData<FleaMarketItemWithFavorites>(
    queryKeys.fleamarket.itemDetail(itemId),
  ),
  itemLists: queryClient.getQueriesData<
    InfiniteData<FleaMarketItemWithFavorites[]>
  >({ queryKey: queryKeys.fleamarket.items() }),
});

/** スナップショットから fav_count 関連キャッシュを復元する */
const restoreFavCountCaches = (itemId: string, snapshot: FavCountSnapshot) => {
  queryClient.setQueryData(
    queryKeys.fleamarket.itemDetail(itemId),
    snapshot.itemDetail,
  );
  for (const [key, data] of snapshot.itemLists) {
    queryClient.setQueryData(key, data);
  }
};

/** サーバー確定後に fav_count 関連キャッシュを真値へ同期する */
const invalidateFavCountCaches = (itemId: string) => {
  // 商品詳細はいいね操作元の画面なので即時 refetch（単一クエリで軽量）
  queryClient.invalidateQueries({
    queryKey: queryKeys.fleamarket.itemDetail(itemId),
  });
  // 商品一覧群は楽観更新で既に正値。stale マークのみ付け次回マウント時に取得し、
  // 表示中の全一覧（メイン/出品者/プロフィールカルーセル・無限スクロール全ページ）の
  // 即時 refetch を避ける。
  queryClient.invalidateQueries({
    queryKey: queryKeys.fleamarket.items(),
    refetchType: "none",
  });
};

/** お気に入り追加（楽観的更新） */
export const addFleaMarketFavoriteMutationOptions = mutationOptions({
  mutationFn: (data: FavoriteMutationVariables) =>
    apiClient<unknown>(ADD_FLEA_MARKET_FAVORITE, {
      user_id: useAuthStore.getState().userId,
      item_id: data.itemId,
    }),
  onMutate: async (variables) => {
    // いいね数を即時 +1（商品詳細・一覧）
    await queryClient.cancelQueries({
      queryKey: queryKeys.fleamarket.itemDetail(variables.itemId),
    });
    await queryClient.cancelQueries({
      queryKey: queryKeys.fleamarket.items(),
    });
    const favCount = snapshotFavCountCaches(variables.itemId);
    adjustFavCountInCaches(variables.itemId, 1);

    // heart 点灯判定用の favorites 一覧（追加分の楽観挿入はせず invalidate に委ねる）
    const userId = useAuthStore.getState().userId;
    const key = userId ? queryKeys.fleamarket.favorites(userId) : undefined;
    let previous: FavoriteItem[] | undefined;
    if (key) {
      await queryClient.cancelQueries({ queryKey: key });
      previous = queryClient.getQueryData<FavoriteItem[]>(key);
    }
    return { previous, key, favCount, itemId: variables.itemId };
  },
  onError: (_err, _variables, context) => {
    if (context?.previous && context?.key) {
      queryClient.setQueryData(context.key, context.previous);
    }
    if (context?.favCount) {
      restoreFavCountCaches(context.itemId, context.favCount);
    }
  },
  onSettled: (_data, _err, _variables, context) => {
    if (context?.key) {
      queryClient.invalidateQueries({ queryKey: context.key });
    }
    if (context?.itemId) {
      invalidateFavCountCaches(context.itemId);
    }
  },
});

/** お気に入り削除（楽観的更新） */
export const removeFleaMarketFavoriteMutationOptions = mutationOptions({
  mutationFn: (data: FavoriteMutationVariables) =>
    apiClient<unknown>(REMOVE_FLEA_MARKET_FAVORITE, {
      user_id: useAuthStore.getState().userId,
      item_id: data.itemId,
    }),
  onMutate: async (variables) => {
    // いいね数を即時 -1（商品詳細・一覧）
    await queryClient.cancelQueries({
      queryKey: queryKeys.fleamarket.itemDetail(variables.itemId),
    });
    await queryClient.cancelQueries({
      queryKey: queryKeys.fleamarket.items(),
    });
    const favCount = snapshotFavCountCaches(variables.itemId);
    adjustFavCountInCaches(variables.itemId, -1);

    // heart を即時消灯（favorites 一覧から該当 item を除外）
    const userId = useAuthStore.getState().userId;
    const key = userId ? queryKeys.fleamarket.favorites(userId) : undefined;
    let previous: FavoriteItem[] | undefined;
    if (key) {
      await queryClient.cancelQueries({ queryKey: key });
      previous = queryClient.getQueryData<FavoriteItem[]>(key);
      queryClient.setQueryData<FavoriteItem[]>(
        key,
        (old) => old?.filter((f) => f.item_id !== variables.itemId) ?? [],
      );
    }
    return { previous, key, favCount, itemId: variables.itemId };
  },
  onError: (_err, _variables, context) => {
    if (context?.previous && context?.key) {
      queryClient.setQueryData(context.key, context.previous);
    }
    if (context?.favCount) {
      restoreFavCountCaches(context.itemId, context.favCount);
    }
  },
  onSettled: (_data, _err, _variables, context) => {
    if (context?.key) {
      queryClient.invalidateQueries({ queryKey: context.key });
    }
    if (context?.itemId) {
      invalidateFavCountCaches(context.itemId);
    }
  },
});

/** 取引作成（購入） */
export const createTransactionMutationOptions = mutationOptions({
  mutationFn: (data: { itemId: string }) =>
    apiClient<{ transaction_id: string }>(CREATE_FLEA_MARKET_TRANSACTION, {
      item_id: data.itemId,
      buyer_id: useAuthStore.getState().userId,
    }),
  onSuccess: () => {
    const userId = useAuthStore.getState().userId;
    if (userId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.fleamarket.transactions(userId),
      });
    }
  },
});

/** 取引ステータス更新 */
export const updateTransactionMutationOptions = mutationOptions({
  mutationFn: (data: { transactionId: string; status: TransactionStatus }) =>
    apiClient<unknown>(UPDATE_FLEA_MARKET_TRANSACTION, {
      transaction_id: data.transactionId,
      status: data.status,
    }),
  onSuccess: (_data, variables) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.fleamarket.transactionDetail(variables.transactionId),
    });
    const userId = useAuthStore.getState().userId;
    if (userId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.fleamarket.transactions(userId),
      });
    }
  },
});

/** 配送ステータス更新（受け取り確認） */
export const updateShipmentStatusMutationOptions = mutationOptions({
  mutationFn: (data: { shippingId: string; transactionId: string }) =>
    apiClient<unknown>(UPDATE_FLEA_MARKET_SHIPMENT_STATUS, {
      user_id: useAuthStore.getState().userId,
      shipping_id: data.shippingId,
      status: "received",
      notes: "商品を受け取りました",
    }),
  onSuccess: (_data, variables) => {
    const userId = useAuthStore.getState().userId;
    queryClient.invalidateQueries({
      queryKey: queryKeys.fleamarket.transactionDetail(variables.transactionId),
    });
    if (userId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.fleamarket.shipmentDetail(
          userId,
          variables.transactionId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.fleamarket.transactions(userId),
      });
    }
  },
});

/** 配送先住所登録 */
export const registerAddressMutationOptions = mutationOptions({
  mutationFn: (data: RegisterAddressPayload) =>
    apiClient<unknown>(REGISTER_ADDRESS_FLEA_MARKET, {
      user_id: useAuthStore.getState().userId,
      last_name: data.last_name,
      first_name: data.first_name,
      postal_code: data.postal_code,
      prefecture: data.prefecture,
      city: data.city,
      address_line1: data.address_line1,
      ...(data.address_line2 !== undefined
        ? { address_line2: data.address_line2 }
        : {}),
      phone_number: data.phone_number,
      is_default: data.is_default ?? true,
    }),
  onSuccess: () => {
    const userId = useAuthStore.getState().userId;
    if (userId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.fleamarket.address(userId),
      });
    }
  },
});

/** util: FleaMarketItemWithFavorites → FleaMarketItem に潰す */
export const flattenItemWithFav = (
  entry: FleaMarketItemWithFavorites,
): FleaMarketItem => {
  return { ...entry.item, fav_count: entry.fav_count };
};
